import { IConexaResponse, IEnumResponse } from '@/types'
import {
  ClinicListData,
  ClinicListDataMapper,
} from './mappers/ClinicListMapper/ClinicListMapper.types'
import ClinicListMapper from './mappers/ClinicListMapper/ClinicListMapper'
import AddressByCepMapper from './mappers/AddressByCepMapper/AddressByCepMapper'
import coreApi from '../coreApi'
import EnumToDropdownDataMapper from './mappers/EnumToDropdownDataMapper/EnumToDropdownDataMapper'
import { AddressByCepData } from './mappers/AddressByCepMapper/AddressByCepMapper.types'
import { Cbo } from './queries/type'
import HolderHealthPlanMapper from './mappers/HolderHealthPlanMapper/HolderHealthPlanMapper'
import { HolderHealthPlanData } from './mappers/HolderHealthPlanMapper/HolderHealthPlanMapper.types'
import { replaceCPF } from '@/utils/replaceCPF'
import { UserDetails } from '@/modules/Users/services/ConexaAdminServices/ConexaAdminServices.types'
import ConexaAdminUserDetailsMapper from '@/modules/Users/services/mapper/ConexaAdminMapper/ConexaAdminUserDetailsMapper'
import { StorageKeys } from '@/contexts/Auth/types'

class GeneralService {
  static async getClinicList(
    clinicName: string,
  ): Promise<ClinicListDataMapper[]> {
    const {
      data: { object },
    } = await coreApi.get<IConexaResponse<ClinicListData[]>>(
      `clinica/autocomplete?name=${clinicName}`,
    )

    return object.map(ClinicListMapper.toDomain)
  }

  static async getOwnershipTypes() {
    const {
      data: { object },
    } = await coreApi.get<IConexaResponse<IEnumResponse[]>>(
      `paciente/list/titularidade`,
    )

    return object.map(item => EnumToDropdownDataMapper.toDomain(item))
  }

  static async getGenderList() {
    const {
      data: { object },
    } = await coreApi.get<IConexaResponse<IEnumResponse[]>>(
      `/user/enums/gender`,
    )

    return object.map(item => EnumToDropdownDataMapper.toDomain(item))
  }

  static async getRelationshipList() {
    const {
      data: { object },
    } = await coreApi.get<IConexaResponse<IEnumResponse[]>>(
      `user/enums/kinship`,
    )

    return object.map(item => EnumToDropdownDataMapper.toDomain(item))
  }

  static async getRelationshipContactEmergencyList() {
    const {
      data: { object },
    } = await coreApi.get<IConexaResponse<IEnumResponse[]>>(
      `/paciente/list/relationship-type`,
    )

    return object.map(item => EnumToDropdownDataMapper.toDomain(item))
  }

  static async getAddressByCep(cep: string) {
    const {
      data: { object },
    } = await coreApi.get<IConexaResponse<AddressByCepData>>(`/site/cep/${cep}`)

    return AddressByCepMapper.toDomain(object)
  }

  static async getBooleanList() {
    const {
      data: { object },
    } = await coreApi.get<IConexaResponse<IEnumResponse[]>>(
      `user/enums/boolean-type`,
    )

    return object.map(item => EnumToDropdownDataMapper.toDomain(item))
  }

  static async getStateList() {
    const {
      data: { object },
    } = await coreApi.get<IConexaResponse<IEnumResponse[]>>(`/user/uf`)

    return object.map(item =>
      EnumToDropdownDataMapper.toDomain(item, {
        autoCapitalize: false,
      }),
    )
  }

  static async getGenderIdentityList() {
    const {
      data: { object },
    } = await coreApi.get<IConexaResponse<IEnumResponse[]>>(`/user/gender`)

    return object.map(item => EnumToDropdownDataMapper.toDomain(item))
  }

  static async getRaceColorList() {
    const {
      data: { object },
    } = await coreApi.get<IConexaResponse<IEnumResponse[]>>(`/user/race-color`)

    return object.map(item => EnumToDropdownDataMapper.toDomain(item))
  }

  static async getReligionList() {
    const {
      data: { object },
    } = await coreApi.get<IConexaResponse<IEnumResponse[]>>(`/user/religion`)

    return object.map(item => EnumToDropdownDataMapper.toDomain(item))
  }

  static async getEducationList() {
    const {
      data: { object },
    } = await coreApi.get<IConexaResponse<IEnumResponse[]>>(`/user/education`)

    return object.map(item => EnumToDropdownDataMapper.toDomain(item))
  }

  static async getLivingAreaList() {
    const {
      data: { object },
    } = await coreApi.get<IConexaResponse<IEnumResponse[]>>(`/user/living-area`)

    return object.map(item => EnumToDropdownDataMapper.toDomain(item))
  }

  static async getCboListByName(params: string) {
    const {
      data: { object },
    } = await coreApi.get<IConexaResponse<Cbo[]>>(`/user/cbos?filter=${params}`)

    return object
  }

  static async getCboListById(params: string | number) {
    const {
      data: { object },
    } = await coreApi.get<IConexaResponse<Cbo[]>>(`/user/cbos?id=${params}`)

    return object
  }

  static async getNationalityList() {
    const {
      data: { object },
    } = await coreApi.get<IConexaResponse<IEnumResponse[]>>('/user/nationality')

    return object.map(item => EnumToDropdownDataMapper.toDomain(item))
  }

  static async getCountryList() {
    const {
      data: { object },
    } = await coreApi.get<IConexaResponse<IEnumResponse[]>>('/user/country')

    return object.map(item => EnumToDropdownDataMapper.toDomain(item))
  }

  static async getHealthPlanHolderByNameOrCpf(
    term: string,
    idClinic: string | number,
  ) {
    const {
      data: { object },
    } = await coreApi.get<IConexaResponse<HolderHealthPlanData[]>>(
      `/paciente/buscar/titular/nome-ou-cpf?termo=${replaceCPF(
        term,
      )}&idClinica=${idClinic}`,
    )

    return object.map(HolderHealthPlanMapper.toDomain)
  }

  static async getConexaAdminPermissions(userId: string): Promise<UserDetails> {
    const token = localStorage.getItem(StorageKeys.token)

    const {
      data: { object },
    } = await coreApi.get(`user/admin/${userId}`, { headers: { token } })

    return ConexaAdminUserDetailsMapper.toDomain(object)
  }
}

export { GeneralService }
